import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaSave, FaArrowLeft, FaImage, FaGlobe } from 'react-icons/fa';
import { useArticles } from '../../hooks/useArticles';
import { useAuth } from '../../context/AuthContext';
import { API_BASE } from '../../config/api';
import RichTextEditor from '../../components/admin/RichTextEditor';
import ImagePicker from '../../components/admin/ImagePicker';

const CATEGORIES = ['actualités', 'annonce', 'événement', 'vie quotidienne'];

function ArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getArticle, createArticle, updateArticle } = useArticles();
  const { token } = useAuth();
  const isEditing = !!id;

  const [form, setForm] = useState({
    title: '',
    date: new Date().toISOString().split('T')[0],
    category: CATEGORIES[0],
    excerpt: '',
    content: '',
    featuredImage: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [translationCount, setTranslationCount] = useState(0);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    if (isEditing) {
      setLoading(true);
      getArticle(id).then(article => {
        setForm({
          title: article.title || '',
          date: article.date || '',
          category: article.category || CATEGORIES[0],
          excerpt: article.excerpt || '',
          content: article.content || '',
          featuredImage: article.featuredImage || ''
        });
        setTranslationCount(article.translations ? Object.keys(article.translations).length : 0);
      }).catch(() => {
        alert('Article non trouvé');
        navigate('/admin/articles');
      }).finally(() => setLoading(false));
    }
  }, [id, isEditing, getArticle, navigate]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { alert('Le titre est obligatoire'); return; }
    setSaving(true);
    try {
      if (isEditing) {
        await updateArticle(id, form);
      } else {
        await createArticle(form);
      }
      navigate('/admin/articles');
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRetranslate = async () => {
    setTranslating(true);
    try {
      const res = await fetch(`${API_BASE}/api/articles/${id}/translate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to trigger translation');
      alert('Traduction relancée en arrière-plan');
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setTranslating(false);
    }
  };

  if (loading) return <p style={{ color: '#888', textAlign: 'center', padding: '40px' }}>Chargement...</p>;

  return (
    <div>
      <div className="admin-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={() => navigate('/admin/articles')} className="admin-btn admin-btn-secondary" style={{ padding: '8px 12px' }}>
            <FaArrowLeft />
          </button>
          <h1>{isEditing ? 'Modifier l\'article' : 'Nouvel article'}</h1>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {isEditing && (
            <>
              <span style={{ fontSize: '13px', color: '#888' }}>
                <FaGlobe style={{ marginRight: '4px' }} />
                {translationCount}/13 traductions
              </span>
              <button onClick={handleRetranslate} disabled={translating} className="admin-btn admin-btn-secondary" style={{ opacity: translating ? 0.7 : 1 }}>
                <FaGlobe /> {translating ? 'Traduction...' : 'Retraduire'}
              </button>
            </>
          )}
          <button onClick={() => navigate('/admin/articles')} className="admin-btn admin-btn-secondary">
            Annuler
          </button>
          <button onClick={handleSubmit} disabled={saving} className="admin-btn admin-btn-primary" style={{ opacity: saving ? 0.7 : 1 }}>
            <FaSave /> {saving ? 'Enregistrement...' : (isEditing ? 'Mettre à jour' : 'Publier')}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={{ maxWidth: '960px' }}>
        <div className="admin-card" style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '20px' }}>
            <label className="admin-label">Titre</label>
            <input type="text" value={form.title} onChange={e => handleChange('title', e.target.value)} className="admin-input" style={{ fontSize: '18px', padding: '14px' }} required placeholder="Titre de l'article" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label className="admin-label">Date de publication</label>
              <input type="date" value={form.date} onChange={e => handleChange('date', e.target.value)} className="admin-input" />
            </div>
            <div>
              <label className="admin-label">Catégorie</label>
              <select value={form.category} onChange={e => handleChange('category', e.target.value)} className="admin-input">
                {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label className="admin-label">Extrait</label>
            <textarea
              value={form.excerpt}
              onChange={e => handleChange('excerpt', e.target.value)}
              rows={3}
              className="admin-input"
              style={{ resize: 'vertical' }}
              placeholder="Court résumé affiché dans la liste des articles..."
            />
          </div>

          <div>
            <label className="admin-label">Image mise en avant</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="text"
                value={form.featuredImage}
                onChange={e => handleChange('featuredImage', e.target.value)}
                className="admin-input"
                style={{ flex: 1 }}
                placeholder="/uploads/..."
              />
              <button
                type="button"
                onClick={() => setShowImagePicker(true)}
                className="admin-btn admin-btn-secondary"
              >
                <FaImage /> Parcourir
              </button>
            </div>
            {form.featuredImage && (
              <img src={form.featuredImage} alt="Preview" style={{ marginTop: '10px', maxHeight: '160px', borderRadius: '8px', border: '1px solid #eee' }} />
            )}
          </div>
        </div>

        <div className="admin-card" style={{ marginBottom: '20px' }}>
          <label className="admin-label" style={{ marginBottom: '12px' }}>Contenu de l'article</label>
          <RichTextEditor value={form.content} onChange={val => handleChange('content', val)} />
        </div>
      </form>

      {showImagePicker && (
        <ImagePicker
          onSelect={(path) => { handleChange('featuredImage', path); setShowImagePicker(false); }}
          onClose={() => setShowImagePicker(false)}
        />
      )}
    </div>
  );
}

export default ArticleEditor;
